import {IconArrowUp, IconChevronDown} from "@tabler/icons-react";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import chicaSoporte from "../../assets/images/chica-soporte.png";
import plus18Icon from "../../assets/images/footer-logos/plus18.svg";
import appStoreLogo from "../../assets/images/icon-apple.svg";
import playStoreLogo from "../../assets/images/icon-google-play.svg";
import instalaAppWin from "../../assets/images/instala-app-win.png";
import conalotLogo from "../../assets/images/LOGOS BANCOS Y METODOS DE PAGO/CONALOT.png";
import cncLogo from "../../assets/images/LOGOS BANCOS Y METODOS DE PAGO/cnc.png";
import inhLogo from "../../assets/images/LOGOS BANCOS Y METODOS DE PAGO/INH.png";
import facebookIcon from "../../assets/images/social-media/facebookIcon.svg";
import instagramIcon from "../../assets/images/social-media/instagramIcon.svg";
import tiktokIcon from "../../assets/images/social-media/tiktokIcon.svg";
import xIcon from "../../assets/images/social-media/xIcon.svg";
import tarjetaPromo from "../../assets/images/tarjeta-promo.png";
import {DownloadApp, SocialMedia} from "../../config/config";
import {useAuthStore} from "../../store/useAuth.store";
import {useUserSupport} from "../../store/useUserSupport.store";
import {getCompanyName, useBrandTitle, useMonogram} from "../../util/logoUtils";
import {FlagIcon} from "../FlagIcon";
import {StoriesCarousel} from "../Stories";
import styles from "./Footer.module.css";

const LANGS = [
  {code: "es", flagCode: "es", label: "ES"},
  {code: "en", flagCode: "us", label: "EN"},
  {code: "zh", flagCode: "cn", label: "ZH"},
];

const Footer = () => {
  const {t, i18n} = useTranslation();
  const navigate = useNavigate();
  const brandTitle = useBrandTitle();
  const companyName = getCompanyName();
  const monogram = useMonogram();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const handleSupportModal = useUserSupport(state => state.handleSupportModal);

  const [openSection, setOpenSection] = useState(null);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const currentLang = LANGS.find(l => i18n.language.startsWith(l.code)) || LANGS[0];

  useEffect(() => {
    const handleClick = e => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const toggleSection = key => {
    setOpenSection(prev => (prev === key ? null : key));
  };

  const handleNav = path => {
    if (authStatus !== "authenticated") {
      handleShowAuthModal();
      return;
    }
    navigate(path);
    setTimeout(() => window.scrollTo({top: 0, behavior: "smooth"}), 100);
  };

  const infoLinks = [
    {label: t("footerTitleHelp"), href: "https://soporte.randrush.com/hc/help/en"},
    {label: t("footerTitlePromotions"), href: "https://soporte.randrush.com/hc/help/en/categories/promociones"},
    {
      label: t("footerTitleTerms"),
      action: () => {
        navigate("/terminos-y-condiciones");
        setTimeout(() => window.scrollTo({top: 0, behavior: "smooth"}), 100);
      },
    },
  ];

  const categoryLinks = [
    {label: t("footerTitleGameCasino"), action: () => handleNav("/casino")},
    {label: t("footerTitleSports"), action: () => handleNav("/sports")},
    {label: t("footerTitleGameLottery"), action: () => handleNav("/lottery")},
    {label: t("footerTitleGameGames"), action: () => handleNav("/gameslist/all")},
    {label: t("footerTitleGameSlots"), action: () => handleNav("/gameslist/all")},
    {label: t("footerTitleGameMinigame"), action: () => handleNav("/gameslist/minigames")},
    {label: t("footerTitleTestimonials"), action: () => handleNav("/testimonios")},
    {label: t("footerTitleNews"), action: () => handleNav("/news")},
  ];

  const socialLinks = [
    {icon: instagramIcon, label: "Instagram", href: SocialMedia.INSTAGRAM},
    {icon: facebookIcon, label: "Facebook", href: SocialMedia.FACEBOOK},
    {icon: xIcon, label: "X", href: SocialMedia.X},
    {icon: tiktokIcon, label: "TikTok", href: SocialMedia.TIKTOK},
  ];

  const renderAccordion = (key, title, children) => {
    const isOpen = openSection === key;
    return (
      <div className={styles.accordionSection}>
        <button
          type="button"
          className={styles.accordionHeader}
          onClick={() => toggleSection(key)}
          aria-expanded={isOpen}
        >
          {title}
          <IconChevronDown
            size={18}
            className={`${styles.accordionChevron} ${isOpen ? styles.accordionChevronOpen : ""}`}
          />
        </button>
        <div className={`${styles.accordionBody} ${isOpen ? styles.accordionBodyOpen : ""}`}>
          <div className={styles.accordionBodyInner}>{children}</div>
        </div>
      </div>
    );
  };

  const scrollToTop = () => window.scrollTo({top: 0, behavior: "smooth"});

  return (
    <footer className={styles.footer}>
      <div className={styles.footerInner}>
        {/* ── Top bar: monogram + line + language + scroll-to-top ── */}
        <section className={styles.topBar}>
          <img
            src={monogram}
            alt={brandTitle}
            className={styles.topBarLogo}
          />
          <hr className={styles.topBarDivider} />
          <div className={styles.topBarRight}>
            <div
              className={styles.langPicker}
              ref={langRef}
            >
              <button
                type="button"
                className={styles.langBtn}
                onClick={() => setLangOpen(prev => !prev)}
                aria-expanded={langOpen}
              >
                <FlagIcon
                  code={currentLang.flagCode}
                  size={18}
                />
                {currentLang.label}
                <IconChevronDown
                  size={14}
                  className={`${styles.langChevron} ${langOpen ? styles.langChevronOpen : ""}`}
                />
              </button>
              {langOpen && (
                <ul className={styles.langDropdown}>
                  {LANGS.filter(l => l.code !== currentLang.code).map(l => (
                    <li key={l.code}>
                      <button
                        type="button"
                        className={styles.langOption}
                        onClick={() => {
                          i18n.changeLanguage(l.code);
                          setLangOpen(false);
                        }}
                      >
                        <FlagIcon
                          code={l.flagCode}
                          size={18}
                        />
                        {l.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              type="button"
              className={styles.scrollTopBtn}
              onClick={scrollToTop}
              aria-label="Scroll to top"
            >
              <IconArrowUp size={18} />
            </button>
          </div>
        </section>

        {/* ── Zone 2: Support card + download badges ── */}
        <section className={styles.supportRow}>
          <div className={styles.supportCard}>
            <div className={styles.supportCardInner}>
              <div className={styles.supportInfo}>
                <p className={styles.supportBadge}>
                  {t("footerTitleSupport")} <span className={styles.badge24}>24/7</span>
                </p>
                <p className={styles.supportText}>{t("footerSupportDesc")}</p>
                <button
                  type="button"
                  className={styles.supportBtn}
                  onClick={handleSupportModal}
                >
                  {t("footerContactSupport")}
                </button>
              </div>
              <img
                src={chicaSoporte}
                alt={t("footerTitleSupport")}
                className={styles.supportImage}
                loading="lazy"
              />
            </div>
          </div>

          <div className={styles.downloadColumn}>
            <div className={styles.downloadBadges}>
              <a
                href={DownloadApp.ANDROID}
                target="_blank"
                rel="noreferrer"
                className={styles.downloadBadge}
              >
                <img
                  src={playStoreLogo}
                  alt="Google Play"
                  className={styles.downloadBadgeIcon}
                  loading="lazy"
                />
                <div className={styles.downloadBadgeText}>
                  <span className={styles.downloadBadgeLabel}>{t("footerGetItOn")}</span>
                  <span className={styles.downloadBadgeStore}>Google Play</span>
                </div>
              </a>
              <a
                href={DownloadApp.IOS}
                target="_blank"
                rel="noreferrer"
                className={styles.downloadBadge}
              >
                <img
                  src={appStoreLogo}
                  alt="App Store"
                  className={styles.downloadBadgeIcon}
                  loading="lazy"
                />
                <div className={styles.downloadBadgeText}>
                  <span className={styles.downloadBadgeLabel}>{t("footerDownloadOn")}</span>
                  <span className={styles.downloadBadgeStore}>App Store</span>
                </div>
              </a>
            </div>

            <div className={styles.winCard}>
              <div className={styles.winCardContent}>
                <p className={styles.winTitle}>{t("footerWinAppTitle")}</p>
                <button
                  type="button"
                  className={styles.winBtn}
                  onClick={() => {
                    const evt = window.__pwaInstallPrompt;
                    if (evt) {
                      evt.prompt();
                    }
                  }}
                >
                  <svg
                    className={styles.winLogo}
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
                  </svg>
                  {t("footerInstallBtn")}
                </button>
              </div>
              <img
                src={instalaAppWin}
                alt={t("footerWinAppTitle")}
                className={styles.winImage}
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* ── Zone 3: Link columns + promo cards ── */}
        <section className={styles.contentGrid}>
          <div className={styles.accordionsWrapper}>
            {renderAccordion(
              "info",
              t("footerInfoTitle"),
              <ul className={styles.accordionList}>
                {infoLinks.map(link => (
                  <li key={link.label}>
                    {link.href ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.accordionLink}
                      >
                        {link.label}
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={link.action}
                        className={styles.accordionLink}
                      >
                        {link.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {renderAccordion(
              "categories",
              t("footerCategoriesTitle"),
              <ul className={styles.accordionList}>
                {categoryLinks.map(link => (
                  <li key={link.label}>
                    <span
                      className={styles.accordionLink}
                      onClick={link.action}
                      role="link"
                      tabIndex={0}
                      onKeyDown={e => e.key === "Enter" && link.action()}
                    >
                      {link.label}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {renderAccordion(
              "support",
              t("footerTitleSupport"),
              <ul className={styles.accordionList}>
                <li>
                  <span
                    className={styles.accordionLink}
                    onClick={handleSupportModal}
                    role="link"
                    tabIndex={0}
                    onKeyDown={e => e.key === "Enter" && handleSupportModal()}
                  >
                    {t("footerContactSupport")}
                  </span>
                </li>
                <li>
                  <a
                    href="https://soporte.randrush.com/hc/help/en"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.accordionLink}
                  >
                    {t("footerTitleHelp")}
                  </a>
                </li>
              </ul>
            )}
          </div>

          <div className={styles.appCards}>
            <div
              className={styles.promoCard}
              onClick={handleShowAuthModal}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === "Enter" && handleShowAuthModal()}
            >
              <div className={styles.promoCardContent}>
                <div className={styles.promoCardText}>
                  <p className={styles.promoTitle}>{t("footerPromoTitle")}</p>
                  <p className={styles.promoDesc}>{t("footerPromoDesc")}</p>
                  <p className={styles.promoAvailable}>{t("footerPromoAvailable")}</p>
                  <span className={styles.promoBtn}>{t("footerRegisterBtn")}</span>
                </div>
                <img
                  src={tarjetaPromo}
                  alt={t("footerPromoTitle")}
                  className={styles.promoImage}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── Zone 4: Bottom band — social + providers + regulatory ── */}
        <section className={styles.bottomBand}>
          <div className={styles.socialSection}>
            {socialLinks.map(({icon, label, href}) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                className={styles.socialLink}
                aria-label={label}
              >
                <img
                  src={icon}
                  alt=""
                  className={styles.socialIcon}
                />
              </a>
            ))}
          </div>

          <div className={styles.providersSection}>
            <StoriesCarousel />
          </div>

          <div className={styles.regulatoryCards}>
            <div className={styles.regulatoryCardLogos}>
              <img
                src={cncLogo}
                alt="CNC"
                className={styles.regulatoryLogo}
                loading="lazy"
              />
              <img
                src={inhLogo}
                alt="INH"
                className={styles.regulatoryLogo}
                loading="lazy"
              />
              <img
                src={conalotLogo}
                alt="CONALOT"
                className={styles.regulatoryLogo}
                loading="lazy"
              />
              <img
                src={plus18Icon}
                alt="18+"
                className={styles.responsibleBadge}
              />
            </div>
          </div>
        </section>

        {/* ── Zone 5: Copyright ── */}
        <section className={styles.copyrightSection}>
          <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: "4px"}}>
            <p className={styles.copyrightText}>
              © {new Date().getFullYear()} {brandTitle} | {companyName}
              <span
                className={styles.plus18}
                style={{marginLeft: "8px"}}
              >
                18+
              </span>
            </p>
            <p
              className={styles.copyrightText}
              style={{opacity: 0.6}}
            >
              v{import.meta.env.PACKAGE_VERSION}
            </p>
          </div>
        </section>
      </div>
    </footer>
  );
};

export default Footer;
